import React, { useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { ApiCall } from "components/common/services/ApiServices";
import CustomNotify from "components/common/atoms/CustomNotify";
import { t } from "components/CentralDataMangement/translation/Translation";
import { CANDIDATE_MICROSERVICE } from "Constants";
import ResumeIcon from "static/images/ResumeIcon";

interface Props {
  candidateId: number | null | undefined;
  /** Optional language key forwarded to the generator (defaults to backend session lang). */
  lang?: string | null;
  title?: string;
}

/**
 * Action that generates the auto-built Absolute Jobs CV and lets the user pick the format.
 *
 * Renders the existing resume icon as a dropdown toggle with two options – PDF and Word –
 * which hit the same `createCandidateResume` endpoint, only differing by the `format` flag.
 * The generated file is opened in a new tab (browser downloads .docx, previews .pdf).
 */
// Custom toggle so the dropdown looks like the bare resume icon (no button chrome / caret).
const IconToggle = React.forwardRef<
  HTMLAnchorElement,
  { onClick: (e: React.MouseEvent) => void; title?: string }
>(({ onClick, title }, ref) => (
  <a
    ref={ref}
    href=""
    title={title}
    className="cursor-pointer"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <ResumeIcon />
  </a>
));
IconToggle.displayName = "ResumeIconToggle";

const ResumeDownloadDropdown: React.FC<Props> = ({
  candidateId,
  lang,
  title,
}) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  // The dropdown opens only on click. These handlers close it when the cursor leaves
  // the icon/menu; entering only cancels a pending close (so hover never opens it),
  // which lets the cursor cross the small gap between icon and menu without closing.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setShow(false), 150);
  };

  const downloadResume = async (format: "pdf" | "docx") => {
    if (!candidateId || loading) {
      return;
    }
    setLoading(true);
    try {
      const response = await ApiCall.service(
        "createCandidateResume",
        "POST",
        { candidateId, format, ...(lang ? { lang } : {}) },
        true,
        CANDIDATE_MICROSERVICE
      );
      if (response?.status === 200 && response?.data?.filePath) {
        window.open(response.data.filePath, "_blank");
      } else {
        CustomNotify({ type: "error", message: t("Something went wrong") });
      }
    } catch (error) {
      CustomNotify({ type: "error", message: t("Something went wrong") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dropdown
      className="d-inline-block"
      show={show}
      onToggle={(nextShow) => setShow(nextShow)}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <Dropdown.Toggle as={IconToggle} title={title ?? t("Download CV")} />
      <Dropdown.Menu className="resume-download-menu" style={{ marginTop: "0.7vw" }}>
        <Dropdown.Item onClick={() => downloadResume("pdf")}>
          {t("Download as PDF")}
        </Dropdown.Item>
        <Dropdown.Item onClick={() => downloadResume("docx")}>
          {t("Download as Word")}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ResumeDownloadDropdown;
